<script>
const NOTIFICATION_MODULES = [
  "Time Logs",
  "Change Shifts",
  "Change Day Types",
  "Official Business",
  "Leaves",
  "Overtime",
  "My Requests",
];

export default {
  data() {
    return {
      show: false,
      toastDuration: 3,
      emailEnabled: true,
      emailIntervalDays: 1,
      emailTime: "00:00",
      moduleFlags: NOTIFICATION_MODULES.map((name) => ({
        name,
        filings: false,
        approvals: false,
      })),
    };
  },
  computed: {
    twoFactorEnabled() {
      return !!this.$store.state.auth.currentUser?.two_factor_enabled;
    },
    emailTimeLabel() {
      if (!this.emailTime) return "";
      const [h, m] = this.emailTime.split(":").map(Number);
      const ap = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ap}`;
    },
  },
  watch: {
    show(v) {
      if (typeof document !== "undefined") {
        if (v) {
          document.body.classList.add("modal-open");
          document.body.style.overflow = "hidden";
        } else {
          document.body.classList.remove("modal-open");
          document.body.style.overflow = "";
        }
      }
    },
  },
  mounted() {
    window.addEventListener("worksched:open-settings", this.open);
  },
  beforeUnmount() {
    window.removeEventListener("worksched:open-settings", this.open);
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
  },
  methods: {
    open() {
      this.show = true;
    },
    close() {
      this.show = false;
    },
    open2fa() {
      // Close settings and hand off to the dedicated 2FA modal (z-index above).
      this.show = false;
      window.dispatchEvent(new CustomEvent("worksched:open-2fa"));
    },
    save() {
      this.show = false;
    },
    tryToast() {
      if (this.$swal) {
        this.$swal({
          title: "Toast test",
          timer: this.toastDuration * 1000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    },
    incInterval() {
      this.emailIntervalDays += 1;
    },
    decInterval() {
      if (this.emailIntervalDays > 1) this.emailIntervalDays -= 1;
    },
    openTimePicker() {
      const el = this.$refs.timeInput;
      if (!el) return;
      if (typeof el.showPicker === "function") el.showPicker();
      else el.focus();
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="ws-settings-backdrop" @click.self="close">
      <div class="ws-settings-dialog">
        <div class="ws-settings-card">
          <header class="ws-settings-header">
            <h5 class="ws-settings-title"><i class="bx bx-cog me-2"></i>Settings</h5>
            <button type="button" class="ws-settings-close" aria-label="Close" @click="close">
              <i class="bx bx-x"></i>
            </button>
          </header>

          <div class="ws-settings-body">
            <!-- Two-Factor Authentication -->
            <section class="ws-card">
              <div class="ws-card__head">
                <span class="ws-card__icon ws-card__icon--secure"><i class="bx bx-shield-quarter"></i></span>
                <div class="ws-card__heading">
                  <h6 class="ws-card__title">
                    Two-Factor Authentication
                    <span v-if="twoFactorEnabled" class="ws-status ws-status--on"><i class="bx bx-check-circle"></i>Enabled</span>
                    <span v-else class="ws-status ws-status--off">Disabled</span>
                  </h6>
                  <p class="ws-card__subtitle">Add a one-time code from an authenticator app at sign-in.</p>
                </div>
                <button type="button" class="ws-btn-outline" @click="open2fa">
                  {{ twoFactorEnabled ? "Manage" : "Enable" }}
                </button>
              </div>
            </section>

            <!-- Toast Notifications -->
            <section class="ws-card">
              <div class="ws-card__head">
                <span class="ws-card__icon ws-card__icon--brand"><i class="bx bx-bell"></i></span>
                <div class="ws-card__heading">
                  <h6 class="ws-card__title">Toast Notifications</h6>
                  <p class="ws-card__subtitle">How long pop-up messages stay on screen.</p>
                </div>
              </div>
              <div class="ws-card__content ws-toast-row">
                <label for="ws-toast-duration" class="ws-field-label">Duration (seconds)</label>
                <input
                  id="ws-toast-duration"
                  v-model.number="toastDuration"
                  type="number"
                  min="1"
                  class="ws-num-input"
                />
                <button type="button" class="ws-link-btn" @click="tryToast"><i class="bx bx-play-circle me-1"></i>Try</button>
              </div>
            </section>

            <!-- Email Notifications -->
            <section class="ws-card">
              <div class="ws-card__head">
                <span class="ws-card__icon ws-card__icon--brand"><i class="bx bx-envelope"></i></span>
                <div class="ws-card__heading">
                  <h6 class="ws-card__title">Email Notifications</h6>
                  <p class="ws-card__subtitle">Choose what lands in your inbox.</p>
                </div>
              </div>

              <div class="ws-card__content">
                <div class="ws-switch-row">
                  <div class="ws-switch-row__text">
                    <span class="ws-switch-row__label">Daily approvals summary</span>
                    <span class="ws-switch-row__hint">Email the total number of requests awaiting approval.</span>
                  </div>
                  <label class="ws-switch">
                    <input v-model="emailEnabled" type="checkbox" />
                    <span class="ws-switch__slider"></span>
                  </label>
                </div>

                <div class="ws-schedule-row" :class="{ 'ws-schedule-row--off': !emailEnabled }">
                  <span class="ws-field-label">Run every</span>
                  <div class="ws-spin">
                    <input
                      v-model.number="emailIntervalDays"
                      type="number"
                      min="1"
                      class="ws-num-input ws-num-input--narrow"
                      :disabled="!emailEnabled"
                    />
                    <div class="ws-spin__btns">
                      <button type="button" class="ws-spin__btn" :disabled="!emailEnabled" @click="incInterval">
                        <i class="bx bx-caret-up"></i>
                      </button>
                      <button type="button" class="ws-spin__btn" :disabled="!emailEnabled" @click="decInterval">
                        <i class="bx bx-caret-down"></i>
                      </button>
                    </div>
                  </div>
                  <span class="ws-field-label">day(s) at</span>
                  <button
                    type="button"
                    class="ws-time-btn"
                    :disabled="!emailEnabled"
                    @click="openTimePicker"
                  >
                    <i class="bx bx-time-five"></i>
                    <span>{{ emailTimeLabel }}</span>
                  </button>
                  <input
                    ref="timeInput"
                    v-model="emailTime"
                    type="time"
                    class="ws-time-input-hidden"
                    :disabled="!emailEnabled"
                  />
                </div>

                <div class="ws-divider"></div>

                <p class="ws-section-note">
                  <i class="bx bx-broadcast me-1"></i>
                  Real-time updates — get an email each time a transaction status changes.
                </p>

                <div class="ws-modules">
                  <div class="ws-modules__head">
                    <span class="ws-modules__module">Module</span>
                    <span class="ws-modules__col">Filings</span>
                    <span class="ws-modules__col">Approvals</span>
                  </div>
                  <div v-for="row in moduleFlags" :key="row.name" class="ws-modules__row">
                    <span class="ws-modules__module">{{ row.name }}</span>
                    <span class="ws-modules__col">
                      <label class="ws-switch ws-switch--sm">
                        <input v-model="row.filings" type="checkbox" />
                        <span class="ws-switch__slider"></span>
                      </label>
                    </span>
                    <span class="ws-modules__col">
                      <label class="ws-switch ws-switch--sm">
                        <input v-model="row.approvals" type="checkbox" />
                        <span class="ws-switch__slider"></span>
                      </label>
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <footer class="ws-settings-footer">
            <button type="button" class="ws-btn-light" @click="close">Cancel</button>
            <button type="button" class="ws-settings-save" @click="save"><i class="bx bx-check me-1"></i>Save changes</button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ws-settings-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ws-fade 0.18s ease-out;
}
.ws-settings-dialog { width: 100%; max-width: 600px; }
.ws-settings-card {
  background: #f6f7f9;
  border-radius: 0.85rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}

/* Header — brand bar (matches the 2FA / password modals) */
.ws-settings-header {
  background: #F57C00;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1.4rem;
}
.ws-settings-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
}
.ws-settings-close {
  background: rgba(255, 255, 255, 0.15);
  border: 0;
  font-size: 1.25rem;
  line-height: 1;
  color: #fff;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.4rem;
}
.ws-settings-close:hover { background: rgba(255, 255, 255, 0.28); }
.ws-settings-body {
  padding: 1.1rem 1.25rem;
  overflow-y: auto;
}

/* Setting card */
.ws-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  margin-bottom: 0.9rem;
}
.ws-card:last-child { margin-bottom: 0; }
.ws-card__head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}
.ws-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 0.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}
.ws-card__icon--brand { background: rgba(245, 124, 0, 0.12); color: #F57C00; }
.ws-card__icon--secure { background: rgba(25, 135, 84, 0.12); color: #198754; }
.ws-card__heading { flex: 1; min-width: 0; }
.ws-card__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.ws-card__subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.85rem;
  color: #868e96;
  line-height: 1.4;
}
.ws-card__content { margin-top: 0.9rem; }

/* Status pill */
.ws-status {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.ws-status--on { background: rgba(25, 135, 84, 0.12); color: #198754; }
.ws-status--off { background: #f1f3f5; color: #868e96; }

/* Buttons */
.ws-btn-outline {
  background: #fff;
  border: 1px solid #F57C00;
  color: #F57C00;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  flex-shrink: 0;
  align-self: center;
}
.ws-btn-outline:hover { background: #F57C00; color: #fff; }
.ws-link-btn {
  background: transparent;
  border: 0;
  color: #F57C00;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.35rem;
  display: inline-flex;
  align-items: center;
}
.ws-link-btn:hover { background: rgba(245, 124, 0, 0.08); }

/* Field labels + numeric inputs */
.ws-field-label { font-size: 0.9rem; color: #495057; }
.ws-num-input {
  border: 1px solid #dee2e6;
  border-radius: 0.45rem;
  background: #fff;
  outline: none;
  font-size: 0.9rem;
  color: #212529;
  padding: 0.35rem 0.5rem;
  width: 72px;
  text-align: center;
  -moz-appearance: textfield;
}
.ws-num-input::-webkit-outer-spin-button,
.ws-num-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.ws-num-input--narrow { width: 52px; }
.ws-num-input:focus { border-color: #F57C00; box-shadow: 0 0 0 0.15rem rgba(245, 124, 0, 0.15); }
.ws-num-input:disabled { color: #adb5bd; background: #f8f9fa; }

.ws-toast-row { display: flex; align-items: center; gap: 0.75rem; }

/* Toggle switch */
.ws-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex-shrink: 0;
}
.ws-switch input { opacity: 0; width: 0; height: 0; }
.ws-switch__slider {
  position: absolute;
  inset: 0;
  cursor: pointer;
  background: #ced4da;
  border-radius: 999px;
  transition: background 0.18s ease;
}
.ws-switch__slider::before {
  content: "";
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.18s ease;
}
.ws-switch input:checked + .ws-switch__slider { background: #F57C00; }
.ws-switch input:checked + .ws-switch__slider::before { transform: translateX(18px); }
.ws-switch input:disabled + .ws-switch__slider { opacity: 0.5; cursor: not-allowed; }
.ws-switch--sm { width: 36px; height: 20px; }
.ws-switch--sm .ws-switch__slider::before { height: 14px; width: 14px; }
.ws-switch--sm input:checked + .ws-switch__slider::before { transform: translateX(16px); }

/* Switch row (label + hint on the left, switch on the right) */
.ws-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.ws-switch-row__text { display: flex; flex-direction: column; }
.ws-switch-row__label { font-size: 0.92rem; font-weight: 500; color: #212529; }
.ws-switch-row__hint { font-size: 0.82rem; color: #868e96; }

/* Schedule row */
.ws-schedule-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
  transition: opacity 0.15s ease;
}
.ws-schedule-row--off { opacity: 0.5; pointer-events: none; }
.ws-spin { display: inline-flex; align-items: center; gap: 0.25rem; }
.ws-spin__btns { display: inline-flex; flex-direction: column; gap: 0; }
.ws-spin__btn {
  background: transparent;
  border: 0;
  color: #F57C00;
  font-size: 0.9rem;
  line-height: 0.85;
  padding: 0;
  cursor: pointer;
}
.ws-spin__btn:disabled { color: #adb5bd; cursor: not-allowed; }
.ws-time-btn {
  background: rgba(245, 124, 0, 0.08);
  border: 0;
  color: #F57C00;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem 0.65rem;
  border-radius: 0.45rem;
}
.ws-time-btn i { font-size: 1.15rem; }
.ws-time-btn:hover { background: rgba(245, 124, 0, 0.16); }
.ws-time-btn:disabled { color: #adb5bd; cursor: not-allowed; background: #f8f9fa; }
.ws-time-input-hidden {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.ws-divider { height: 1px; background: #f1f3f5; margin: 1.1rem 0; }
.ws-section-note {
  color: #868e96;
  font-size: 0.85rem;
  margin: 0 0 0.9rem;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
}
.ws-section-note i { color: #F57C00; margin-top: 0.1rem; }

/* Modules — switch grid */
.ws-modules {
  border: 1px solid #f1f3f5;
  border-radius: 0.6rem;
  overflow: hidden;
}
.ws-modules__head,
.ws-modules__row {
  display: grid;
  grid-template-columns: 1fr 90px 90px;
  align-items: center;
  padding: 0.55rem 0.85rem;
}
.ws-modules__head {
  background: #f8f9fa;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #868e96;
}
.ws-modules__row {
  border-top: 1px solid #f1f3f5;
  font-size: 0.9rem;
  color: #212529;
}
.ws-modules__row:hover { background: #fcfcfd; }
.ws-modules__col { text-align: center; }
.ws-modules__head .ws-modules__col { text-align: center; }

.ws-settings-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 0.85rem 1.25rem;
  background: #fff;
  border-top: 1px solid #e9ecef;
}
.ws-btn-light {
  background: #fff;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0.5rem 1.2rem;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
}
.ws-btn-light:hover { background: #f8f9fa; }
.ws-settings-save {
  background: #F57C00;
  color: #fff;
  border: 0;
  border-radius: 0.5rem;
  padding: 0.5rem 1.4rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.25);
}
.ws-settings-save:hover { background: #EA580C; }

@keyframes ws-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
